import React, { useEffect, useMemo, useState } from "react";
import { Bell, Check, AlertTriangle, Clock, Info, CheckCheck, Filter } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHero, Section, Button, Badge, EmptyState } from "@/components/premium/UI";

const PRIORITY_TONE = { high: "critical", critical: "critical", medium: "pending", low: "primary" };

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | high

  async function refresh() {
    try {
      const [n, s] = await Promise.all([api.get("/notifications"), api.get("/notifications/schedule")]);
      setItems(Array.isArray(n.data) ? n.data : []);
      setSchedule(s.data?.daily || []);
    } catch { toast.error("Could not load notifications"); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function markRead(id) {
    try { await api.post(`/notifications/${id}/read`); refresh(); }
    catch { toast.error("Could not mark as read"); }
  }
  async function markAllRead() {
    const unread = items.filter(i => !i.read);
    if (!unread.length) { toast.info("No unread notifications"); return; }
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(`Marked ${data?.updated ?? unread.length} as read`);
      refresh();
    } catch { toast.error("Could not mark all as read"); }
  }

  const filtered = useMemo(() => {
    if (filter === "unread") return items.filter(i => !i.read);
    if (filter === "high") return items.filter(i => i.priority === "high" || i.priority === "critical");
    return items;
  }, [items, filter]);

  const unreadCount = items.filter(i => !i.read).length;
  const criticalCount = items.filter(i => (i.priority === "high" || i.priority === "critical") && !i.read).length;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <PageHero
        eyebrow="Alerts & reminders"
        title="Notifications"
        subtitle="Every alert, threshold breach and daily reminder routed to your role."
        moduleKind="reports"
        actions={
          <>
            <Badge variant={unreadCount > 0 ? "pending" : "success"} data-testid="notif-unread-badge">{unreadCount} unread</Badge>
            {criticalCount > 0 && <Badge variant="critical" data-testid="notif-critical-badge">{criticalCount} critical</Badge>}
            <Button variant="outline" size="sm" onClick={markAllRead} data-testid="notif-mark-all-read" disabled={unreadCount === 0}>
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </Button>
          </>
        }
      />

      {schedule.length > 0 && (
        <Section title="Daily reminder schedule" description="Fixed windows the whole family relies on." actions={<Badge variant="primary"><Clock className="w-3 h-3" />{schedule.length} windows</Badge>}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="notif-schedule">
            {schedule.map(s => (
              <div key={s.time} className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/30">
                <div className="font-heading text-2xl font-bold tracking-tight text-primary">{s.time}</div>
                <div className="text-sm mt-1 font-medium">{s.title}</div>
                <div className="mt-2"><Badge variant="neutral">{s.category}</Badge></div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section
        title="Alert stream"
        description={`${items.length} total · ${unreadCount} unread`}
        actions={
          <div className="flex items-center gap-1.5 rounded-full bg-muted/60 p-1 border border-border" data-testid="notif-filter">
            {[
              { key: "all", label: "All" },
              { key: "unread", label: "Unread" },
              { key: "high", label: "Critical" },
            ].map(f => (
              <button
                key={f.key}
                data-testid={`notif-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${filter === f.key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <div className="space-y-2 animate-pulse">
            {[1,2,3,4].map(i => <div key={i} className="h-16 rounded-xl bg-muted/40" />)}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={filter === "unread" ? CheckCheck : Bell}
            title={filter === "unread" ? "You're all caught up" : filter === "high" ? "No critical alerts" : "No notifications yet"}
            description={filter === "all" ? "Threshold alerts, reminders and system events will appear here." : "Try switching the filter to see other notifications."}
          />
        ) : (
          <ul className="divide-y divide-border/60" data-testid="notif-list">
            {filtered.map(n => (
              <li
                key={n.id}
                data-testid={`notif-${n.id}`}
                className={`py-3.5 flex items-start gap-3 rounded-lg px-2 transition-colors hover:bg-muted/30 ${n.read ? "opacity-70" : ""}`}
              >
                <div className={`shrink-0 h-9 w-9 rounded-lg grid place-items-center ${
                  n.priority === "high" || n.priority === "critical" ? "bg-destructive/10 text-destructive"
                  : n.priority === "medium" ? "bg-accent/10 text-accent"
                  : "bg-primary/10 text-primary"
                }`}>
                  {n.priority === "high" || n.priority === "critical" ? <AlertTriangle className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`text-sm truncate ${n.read ? "font-medium" : "font-semibold"}`}>{n.title}</div>
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-label="unread" />}
                  </div>
                  <div className="text-sm text-foreground/80 mt-0.5">{n.message}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-2 flex-wrap">
                    <span>{fmtDateTime(n.created_at)}</span>
                    {n.category && <Badge variant="neutral">{n.category}</Badge>}
                    {n.priority && <Badge variant={PRIORITY_TONE[n.priority] || "primary"}>{n.priority}</Badge>}
                  </div>
                </div>
                {!n.read && (
                  <Button size="sm" variant="ghost" onClick={() => markRead(n.id)} data-testid={`notif-mark-${n.id}`} aria-label="Mark as read">
                    <Check className="w-4 h-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
